import { Step } from "react-joyride";
import React from "react";

// Tutorial steps for dashboard pages
// Only dashboard, employees, and chat have tutorials enabled

export const dashboardTutorialSteps: Step[] = [
  {
    target: '[data-tutorial="hours-saved"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Welcome to <strong>Analytics</strong>! Track the <strong>estimated hours your team has saved</strong> using AI automation.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          This helps demonstrate ROI from your AI investment to stakeholders.
        </p>
      </div>
    ),
    title: "Time Saved",
    placement: "left",
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="company-leaderboard"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          See how your team members are using AI with the <strong>Company Leaderboard</strong>.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          Track individual contributions and identify power users who can help train others.
        </p>
      </div>
    ),
    title: "Company Leaderboard",
    placement: "top",
  },
];

export const employeesTutorialSteps: Step[] = [
  {
    target: '[data-tutorial="create-employee-btn"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          <strong>AI Employees</strong> are specialized AI assistants for specific roles in your business.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          Click <strong>"Create Employee"</strong> to get started. Just enter a job title (like "Customer Support Specialist") and the AI will generate a complete persona with name, photo, and role description.
        </p>
      </div>
    ),
    title: "Create an Employee",
    placement: "bottom-end",
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="browse-public-btn"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Discover pre-built employees from the <strong>marketplace</strong>.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          These come fully configured with specialized expertise - add them to your workspace with one click and start using them immediately.
        </p>
      </div>
    ),
    title: "Browse the Marketplace",
    placement: "bottom-end",
  },
  {
    target: '[data-tutorial="manage-departments-btn"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Organize employees into <strong>teams</strong> like Sales, Support, or Marketing.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          Collaborators only see employees in their assigned departments, giving you fine-grained access control.
        </p>
      </div>
    ),
    title: "Organize with Departments",
    placement: "bottom-end",
  },
];

export const chatTutorialSteps: Step[] = [
  {
    target: '[data-tutorial="chat-input"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Welcome to <strong>Chat</strong>! Have conversations with your AI employees to get help with research, writing, analysis, and more.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          Type your message here to start. You can ask questions, request help with tasks, or have the AI analyze documents.
        </p>
      </div>
    ),
    title: "Start a Conversation",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="chat-menu-btn"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Click the <strong>+</strong> button to access more options:
        </p>
        <ul style={{ margin: "12px 0 0 0", paddingLeft: 20 }}>
          <li><strong>Upload Files</strong> - Attach documents or images for AI to analyze</li>
          <li><strong>Chat Settings</strong> - Change AI model, knowledge base, and more</li>
        </ul>
      </div>
    ),
    title: "Chat Options",
    placement: "top",
  },
];

export const knowledgeBaseTutorialSteps: Step[] = [
  {
    target: '[data-tutorial="upload-document"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Welcome to the <strong>Knowledge Base</strong>! This is where you store documents that your AI employees can reference.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          Click <strong>"Upload Document"</strong> to add files. Supported formats include PDF, Word, Excel, TXT, and more.
        </p>
      </div>
    ),
    title: "Upload Documents",
    placement: "bottom-end",
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="create-document"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          Create a <strong>text document</strong> directly in the app.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          Perfect for adding FAQs, product information, or company policies that your AI employees should know.
        </p>
      </div>
    ),
    title: "Create Documents",
    placement: "bottom-end",
  },
  {
    target: '[data-tutorial="import-website"]',
    content: (
      <div>
        <p style={{ margin: 0 }}>
          <strong>Import content from any website</strong> by entering a URL.
        </p>
        <p style={{ margin: "12px 0 0 0" }}>
          The AI will crawl the page and extract the content for your knowledge base.
        </p>
      </div>
    ),
    title: "Import from Web",
    placement: "bottom-end",
  },
];

// Empty arrays for disabled tutorials - keeps exports but shows nothing
export const workflowsTutorialSteps: Step[] = [];
export const listsTutorialSteps: Step[] = [];
export const sequencesTutorialSteps: Step[] = [];
export const inboxTutorialSteps: Step[] = [];
export const contentEngineTutorialSteps: Step[] = [];
export const agentsTutorialSteps: Step[] = [];
export const integrationsTutorialSteps: Step[] = [];
export const collaboratorsTutorialSteps: Step[] = [];
export const imagesTutorialSteps: Step[] = [];
export const audioTutorialSteps: Step[] = [];
export const videoTutorialSteps: Step[] = [];
export const voicesTutorialSteps: Step[] = [];
export const promptStudioTutorialSteps: Step[] = [];
export const profileTutorialSteps: Step[] = [];
export const companiesTutorialSteps: Step[] = [];

// Map of page keys to their tutorial steps
export const tutorialStepsMap: Record<string, Step[]> = {
  dashboard: dashboardTutorialSteps,
  chat: chatTutorialSteps,
  employees: employeesTutorialSteps,
  knowledge_base: knowledgeBaseTutorialSteps,
  workflows: workflowsTutorialSteps,
  lists: listsTutorialSteps,
  sequences: sequencesTutorialSteps,
  inbox: inboxTutorialSteps,
  content_engine: contentEngineTutorialSteps,
  agents: agentsTutorialSteps,
  integrations: integrationsTutorialSteps,
  collaborators: collaboratorsTutorialSteps,
  images: imagesTutorialSteps,
  audio: audioTutorialSteps,
  video: videoTutorialSteps,
  voices: voicesTutorialSteps,
  prompt_studio: promptStudioTutorialSteps,
  profile: profileTutorialSteps,
  companies: companiesTutorialSteps,
};
